<template>
  <div
    data-component-group="businesses-form"
    data-component="edit"
    class="elevation-1"
  >
    <div class="component-body">
      <forms-businesses-add-new
        :item="item"
        confirm-trigger-label="Update business"
        :is-disabled="!canEditDetails"
        :is-loading="isLoading"
        :on-submit="handleDetailsSubmit"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Details"
            :trigger-label="!canEditDetails ? 'Edit details' : 'Close'"
            @toggle="toggleEditDetails"
          />
        </template>
      </forms-businesses-add-new>

      <forms-businesses-business-contacts
        v-if="hasBusinessContacts"
        :items="hasBusinessContacts ? item.business_contacts : []"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Associated business contacts"
            :has-trigger="false"
          />
        </template>
      </forms-businesses-business-contacts>

      <template v-if="$ability.can('assign-manager', 'business') || $ability.can('unassign-manager', 'billing')">
        <forms-businesses-account-managers
          v-if="settings.length > 0"
          :business="item"
          :is-disabled="!canEditAccountManagers"
          :on-submit="onUpdateAccountManagers"
        >
          <template v-slot:beforeForm>
            <layout-section-header
              title="Account managers"
              :trigger-label="!canEditAccountManagers ? 'Edit' : 'Close'"
              @toggle="toggleAccountManagers"
            />
          </template>
        </forms-businesses-account-managers>
      </template>

      <forms-businesses-settings
        v-if="settings.length > 0"
        :settings="settings"
        :on-submit="onUpdateSettings"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Account settings"
            :has-trigger="false"
          />
        </template>
      </forms-businesses-settings>

      <forms-businesses-promo-users
        :business="item"
        :is-disabled="!canEditAdvertisers"
        :on-submit="onUpdateAdvertisers"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Advertisers"
            :has-trigger="false"
          />
        </template>
      </forms-businesses-promo-users>

      <forms-businesses-cpm
        :business="item"
        :is-disabled="!canEditCpmForm"
        :on-submit="onUpdateCpm"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            :title="$t('assigned_advertiser_to_billing_account')"
            :has-trigger="false"
          />
        </template>
      </forms-businesses-cpm>

      <forms-businesses-cpm-prices
        :business="item"
        :is-disabled="!canEditCpmPrices"
        :on-submit="onUpdateCpmPrice"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            :title="$t('custom_cpm_price')"
            :has-trigger="$ability.can('update', 'business')"
            :trigger-label="!canEditCpmPrices ? 'Edit' : 'Close'"
            @toggle="toggleCpmPrices"
          />
        </template>
      </forms-businesses-cpm-prices>

      <forms-businesses-free-impressions
        :business="item"
        :is-disabled="!canEditFreeImpressions"
        :on-submit="onUpdateFreeImpressions"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            :title="$t('add_free_impressions')"
            :has-trigger="$ability.can('update', 'business')"
            :trigger-label="!canEditFreeImpressions ? 'Edit' : 'Close'"
            @toggle="toggleFreeImpressions"
          />
        </template>
      </forms-businesses-free-impressions>

      <forms-businesses-merchants
        :business="item"
        :is-disabled="!canEditMerchants"
        :on-submit="onUpdateMerchants"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Merchants"
            :has-trigger="$ability.can('update', 'business')"
            :trigger-label="!canEditMerchants ? 'Edit' : 'Close'"
            @toggle="toggleMerchants"
          />
        </template>
      </forms-businesses-merchants>

      <forms-businesses-key-accounts
        :business="item"
        :is-disabled="!canEditKeyAccounts"
        :on-submit="onUpdateKeyAccounts"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Key accounts"
            :has-trigger="$ability.can('change-key-status', 'business')"
            :trigger-label="!canEditKeyAccounts ? 'Edit' : 'Close'"
            @toggle="toggleKeyAccounts"
          />
        </template>
      </forms-businesses-key-accounts>

      <!-- TODO: to be removed july 2026 if not used and hided
      <forms-businesses-billing
        :business="item"
        :is-disabled="!canEditBilling"
        :on-submit="onUpdateBilling"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Billing account"
            :has-trigger="$ability.can('unassign-billing-account', 'business')"
            :trigger-label="!canEditBilling ? 'Edit' : 'Close'"
            @toggle="toggleBilling"
          />
        </template>
      </forms-businesses-billing>
      -->

      <forms-businesses-notifications
        :business="item"
        :is-disabled="!canEditNotifications"
        :on-submit="onUpdateNotifications"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Notify me when"
            :trigger-label="!canEditNotifications ? 'Edit' : 'Close'"
            @toggle="toggleNotifications"
          />
        </template>
      </forms-businesses-notifications>

      <forms-businesses-thread-submission-limits
        :business="item"
        :limits="threadSubmissionsLimits"
        :is-disabled="!canEditThreadSubmissionLimits"
        :on-submit="onUpdateThreadSubmissionLimit"
        :on-remove="onRemoveThreadSubmissionLimit"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Deal submission limits"
            :trigger-label="!canEditThreadSubmissionLimits ? 'Edit' : 'Close'"
            @toggle="toggleThreadSubmissionLimits"
          />
        </template>
      </forms-businesses-thread-submission-limits>

      <forms-businesses-thread-submissions
        id="enable-deal-submissions"
        :business="item"
        :is-disabled="!canEditThreadSubmissions"
        :on-submit="onUpdateThreadSubmission"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Enable deal submissions"
            :trigger-label="!canEditThreadSubmissions ? 'Edit' : 'Close'"
            @toggle="toggleThreadSubmissions"
          />
        </template>
      </forms-businesses-thread-submissions>

      <forms-businesses-enable-promotions
        :business="item"
        :is-disabled="!canEditEnablePromotions"
        :on-submit="onUpdateEnablePromotion"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            :title="$t('enable_promotions')"
            :trigger-label="!canEditEnablePromotions ? $t('edit') : $t('close')"
            @toggle="toggleEnablePromotions"
          />
        </template>
      </forms-businesses-enable-promotions>

      <forms-businesses-campaigns
        id="enable-campaigns"
        :business="item"
        :is-disabled="!canEditCampaigns"
        :on-submit="onUpdateCampaigns"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Enable campaigns"
            :trigger-label="!canEditCampaigns ? 'Edit' : 'Close'"
            @toggle="toggleCampaigns"
          />
        </template>
      </forms-businesses-campaigns>

      <forms-businesses-comments
        :business="item"
        :is-disabled="!canEditComments"
        triggerLabel="Enable"
        :on-submit="onUpdateComments"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Allow comments"
            :has-trigger="$ability.can('change-comments-status', 'business')"
            :trigger-label="!canEditComments ? 'Edit' : 'Close'"
            @toggle="toggleComments"
          />
        </template>
      </forms-businesses-comments>

      <forms-businesses-labels
        :business="item"
        :is-disabled="!canEditLabels"
        :on-submit="onUpdateLabels"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Labels"
            :trigger-label="!canEditLabels ? 'Edit' : 'Close'"
            @toggle="toggleLabels"
          />
        </template>
      </forms-businesses-labels>

      <forms-businesses-notes
        :business="item"
        :is-disabled="!canEditNotes"
      >
        <template v-slot:beforeForm>
          <layout-section-header
            title="Notes"
            :trigger-label="!canEditNotes ? 'Edit' : 'Close'"
            @toggle="toggleNotes"
          />
        </template>
      </forms-businesses-notes>
    </div>
  </div>
</template>

<script>
  import defaultPagination from '../../../config/pagination';
  import { isObj, isArr, hasProp, eachProp, isNonEmptyStr } from '../../../utils';

  // TODO: Translate component

  // TODO: Implement 'Enable deal submission' form

  // TODO: Request that the api returns labels for users settings
  const settingLabels = {
    all_threads: 'Allow users to see each other\'s threads',
    require_ean: 'Item number required',
    block_acc: 'Block account'
  };

  export default {
    props: {
      item: {
        type: Object,
        required: true
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      onCancel: {
        type: Function,
        default: undefined
      },
      onSubmit: {
        type: Function,
        default: undefined
      },
    },
    mounted() {
      if (this.$route.hash) {
        setTimeout(() => {
          document.querySelector(this.$route.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 300);
      }
    },
    data() {
      return {
        pagination: {
          ...defaultPagination
        },
        canEditDetails: false,
        canEditBilling: false,
        canEditLabels: false,
        canEditComments: false,
        canEditCampaigns: false,
        canEditThreadSubmissions: false,
        canEditEnablePromotions: false,
        canEditThreadSubmissionLimits: false,
        canEditNotifications: false,
        canEditKeyAccounts: false,
        canEditAdvertisers: false,
        canEditMerchants: false,
        canEditCpmForm: false,
        canEditCpmPrices: false,
        canEditFreeImpressions: false,
        canEditAccountManagers: false,
        canEditNotes: false
      };
    },
    computed: {
      hasBusinessContacts() {
        return isObj(this.item)
          && isArr(this.item.business_contacts)
          && this.item.business_contacts.length > 0;
      },
      settings() {
        const { settings } = this.$store.getters['businesses/activeItem'];
        return this.unwrapSettings(settings);
      },
      threadSubmissionsLimits() {
        const { threadSubmissionLimits } = this.$store.getters['businesses/activeItem'];
        return threadSubmissionLimits;
      },
      canEditAdvertiserBillingAssignmentSection() {
        return isObj(this.item) && this.$ability.can('assign-advertiser-billing', 'business');
      },
    },
    watch: {
      item: {
        immediate: true,
        handler(nextItem) {
        if (isObj(nextItem) && nextItem.id) {
          this.pagination = defaultPagination;
          this.canEditDetails = false;
          this.canEditSettings = false;
          this.canEditBilling = false;
          this.canEditLabels = false;
          this.canEditComments = false;
          this.canEditCampaigns = false;
          this.canEditThreadSubmissions = false;
          this.canEditEnablePromotions = false;
          this.canEditThreadSubmissionLimits = false;
          this.canEditNotifications = false;
          this.canEditKeyAccounts = false;
          this.canEditAdvertisers = false;
          this.canEditMerchants = false;
          this.canEditCpmForm = false;
          this.canEditCpmPrices = false;
          this.canEditFreeImpressions = false;
          this.canEditAccountManagers = false;
          this.canEditNotes = false;

          this.getSettings(nextItem);
          this.getThreadSubmissionsLimits(nextItem);
          this.getEnabledPromotions(nextItem);
          this.getBusinessAdvertisers(nextItem);
        }
        }
      },
    },
    methods: {
      toggleEditDetails() {
        this.canEditDetails = !this.canEditDetails;
      },
      handleDetailsSubmit(business) {
        return this.onSubmit(business).then((res) => {
          this.canEditDetails = false;
          return res;
        });
      },
      onUpdateSettings() {
        const { item, settings } = this;
        const convertedSettings = this.wrapSettings(settings);

        const query = {
          id: item.id,
          settings: convertedSettings
        };

        return this.$store.dispatch('businesses/updateSettings', query);
      },
      onUpdateBilling(account) {
        const query = {
          id: this.item.id,
          billing_account: account.id
        };

        return this.$store.dispatch('businesses/addBillingAccount', query);
      },
      onUpdateLabels({ country, labels }) {
        const query = {
          id: this.item.id,
          country_labels: country.code,
          labels: labels.map(l => l.id)
        };

        return this.$store.dispatch('businesses/addLabels', query);
      },
      onUpdateComments(country) {
        const query = {
          id: this.item.id,
          country_enable_comments: country.code
        };

        return this.$store.dispatch('businesses/addCommentCountry', query);
      },
      onUpdateCampaigns(country) {
        const query = {
          id: this.item.id,
          country_enable_campaigns: country
        };

        return this.$store.dispatch('businesses/addCampaign', query);
      },
      onUpdateThreadSubmission(country) {
        const query = {
          id: this.item.id,
          country: country.code
        };

        return this.$store.dispatch('businesses/addThreadSubmission', query);
      },
      onUpdateEnablePromotion(country) {
        const query = {
          id: this.item.id,
          country: country.code
        };

        return this.$store.dispatch('businesses/addEnabledPromotion', query);
      },
      async onUpdateThreadSubmissionLimit({ weekly, daily, country, start_date, end_date }) {
        const query = {
          id: this.item.id,
          country: country.code,
          max_deals_per_week: weekly,
          max_deals_per_day: daily,
          start_date,
        };

        if (isNonEmptyStr(end_date)) {
          query.end_date = end_date;
        }

        return this.$store.dispatch('businesses/addThreadSubmissionLimit', query);
      },
      async onRemoveThreadSubmissionLimit(limit) {
        const query = {
          business_id: this.item.id,
          id: limit.id
        };

        return this.$store.dispatch('businesses/removeThreadSubmissionLimit', query);
      },
      onUpdateNotifications({ country, notification }) {
        const query = {
          id: this.item.id,
          country_notify: country.code,
          notify_when: notification.code,
        };

        return this.$store.dispatch('businesses/addNotification', query);
      },
      onUpdateKeyAccounts(country) {
        const query = {
          id: this.item.id,
          key_country: country.code
        };

        return this.$store.dispatch('businesses/addKeyAccount', query);
      },
      onUpdateAdvertisers(advertiser) {
        const query = {
          id: this.item.id,
          advertiser
        };

        return this.$store.dispatch('businesses/addBusinessAdvertiser', query);
      },
      onUpdateCpm({ country, advertiser, account }) {
        const query = {
          id: this.item.id,
          country,
          advertiser,
          account
        };

        return this.$store.dispatch('businesses/addBillingAccountAdvertiser', query);
      },
      onUpdateCpmPrice({ country, advertiser, price }) {
        const query = {
          id: this.item.id,
          country,
          advertiser,
          // placement,
          price
        };

        return this.$store.dispatch('businesses/addBusinessCpmPrice', query);
      },
      onUpdateFreeImpressions({ country, impressions, end_date }) {
        const query = {
          businessId: this.item.id,
          country,
          impressions,
          end_date
        };

        return this.$store.dispatch('businesses/addBusinessFreeImpressions', query);
      },
      onUpdateMerchants({ merchant, country }) {
        const query = {
          id: this.item.id,
          merchant,
          country
        };

        return this.$store.dispatch('businesses/addBusinessMerchant', query);
      },
      onUpdateAccountManagers({ manager, country, assign }) {
        const query = {
          id: this.item.id,
          manager: manager.id,
          manager_country: country.code,
          assign: assign ? 1 : 0
        };

        return this.$store.dispatch('businesses/addAccountManager', query);
      },
      unwrapSettings(settings) {
        const nextSettings = [];

        eachProp(settings, (isOn, key) => {
          if (key === 'block_acc' && !this.$ability.can('change-status', 'business')) {
            return false;
          }

          nextSettings.push({
            label: this.getSettingLabel(key),
            key,
            isOn: isOn > 0
          });
        });

        return nextSettings;
      },
      wrapSettings() {
        const nextSettings = {};

        this.settings.forEach(({ isOn, key }) => {
          if (key === 'block_acc' && !this.$ability.can('change-status', 'business')) {
            return false;
          }

          nextSettings[key] = isOn ? 1 : 0;
        });

        return nextSettings;
      },
      getSettingLabel(settingKey) {
        return hasProp(settingLabels, settingKey)
          ? settingLabels[settingKey]
          : settingKey;
      },
      getSettings({ id }) {
        const query = { id };

        return this.$store.dispatch('businesses/getSettings', query);
      },
      getThreadSubmissionsLimits({ id }) {
        const query = { id };
        return this.$store.dispatch('businesses/getThreadSubmissionsLimits', query);
      },
      getEnabledPromotions({ id }) {
        return this.$store.dispatch('businesses/getEnabledPromotions', { id, only_allowed_countries: true });
      },
      getBusinessAdvertisers({ id }) {
        return this.$store.dispatch('businesses/getBusinessAdvertisers', { id });
      },
      toggleBilling() {
        this.canEditBilling = !this.canEditBilling;
      },
      toggleLabels() {
        this.canEditLabels = !this.canEditLabels;
      },
      toggleNotes() {
        this.canEditNotes = !this.canEditNotes;
      },
      toggleComments() {
        this.canEditComments = !this.canEditComments;
      },
      toggleCampaigns() {
        this.canEditCampaigns = !this.canEditCampaigns;
      },
      toggleThreadSubmissions() {
        this.canEditThreadSubmissions = !this.canEditThreadSubmissions;
      },
      toggleEnablePromotions() {
        this.canEditEnablePromotions = !this.canEditEnablePromotions;
      },
      toggleThreadSubmissionLimits() {
        this.canEditThreadSubmissionLimits = !this.canEditThreadSubmissionLimits;
      },
      toggleNotifications() {
        this.canEditNotifications = !this.canEditNotifications;
      },
      toggleKeyAccounts() {
        this.canEditKeyAccounts = !this.canEditKeyAccounts;
      },
      toggleMerchants() {
        this.canEditMerchants = !this.canEditMerchants;
      },
      toggleCpmForm() {
        this.canEditCpmForm = !this.canEditCpmForm;
      },
      toggleCpmPrices() {
        this.canEditCpmPrices = !this.canEditCpmPrices;
      },
      toggleFreeImpressions() {
        this.canEditFreeImpressions = !this.canEditFreeImpressions;
      },
      toggleAccountManagers() {
        this.canEditAccountManagers = !this.canEditAccountManagers;
      }
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
  @use '../../../assets/styles/theme' as *;

  [data-component-group="businesses-form"][data-component="edit"] {
    [data-component-group="business-form"][data-component="thread-submission-limits"] {
      border-top: 1px solid map.get($colors, grey, lighten-2);
    }
  }
</style>
